/**
 * Field Definition Modal Component
 *
 * Purpose: Modal form for creating and editing field definitions with hierarchy configuration support.
 *
 * Architecture: Self-contained modal component with form validation and hierarchy management.
 *
 * Dependencies: React, Material-UI, MetadataService
 * Author: Development Team
 * Created: 2025-01-27
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  SelectChangeEvent,
  Autocomplete,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Public as GlobalIcon,
  Business as TenantIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

import metadataService, {
  FieldDefinition,
  FieldDefinitionFormData,
  TenantInfo,
} from '../../../services/metadataService';
import { generateFieldDefinitionId } from '../../../utils/patterns';
import SourceFieldFilterBuilder from './SourceFieldFilterBuilder';
import FileConfigurationSection, {
  FileConfig,
  DEFAULT_FILE_CONFIG,
} from './FileConfigurationSection';
import DescriptionField, {
  descriptionLengthError,
} from '../../../components/common/DescriptionField';
import { SecondaryButton, DestructiveButton } from '../../../components/common/ActionButtons';
import DialogFooter from '../../../components/common/DialogFooter';

// Constants
const ACCESS_LEVELS = {
  INTERNAL_ONLY: '0',
  CLIENT_READ_ONLY: '1',
  CLIENT_OVERRIDEABLE: '2',
  CLIENT_OVERRIDEABLE_ALL: '3',
} as const;

const DECIMAL_DEFAULTS = {
  STORAGE: 5,
  DISPLAY: 2,
} as const;

// Utility functions
const cleanHierarchyLabel = (label: string): string => {
  return label
    .replace(/^["']|["']$/g, '')
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'");
};

const shouldShowDecimalPlaces = (datatype: string): boolean => {
  return datatype === 'currency' || datatype === 'decimal' || datatype === 'percent';
};

// Validation helper functions
const validateFieldDefId = (fielddefId: string, metadataService: any): string | null => {
  if (!fielddefId.trim()) {
    return 'Field definition ID is required';
  }
  const validation = metadataService.validateFieldDefId(fielddefId);
  if (!validation.valid) {
    return validation.error || 'Invalid field definition ID';
  }
  return null;
};

const validateLabel = (label: string): string | null => {
  if (!label.trim()) {
    return 'Label is required';
  }
  if (label.length > 100) {
    return 'Label must be under 100 characters';
  }
  return null;
};

const validateDecimalPlaces = (
  value: number | null | undefined,
  fieldName: 'storage' | 'display'
): string | null => {
  if (value === null || value === undefined) {
    return `${fieldName === 'storage' ? 'Storage' : 'Display'} decimal places is required`;
  }
  if (value < 0 || value > 10) {
    return `${fieldName === 'storage' ? 'Storage' : 'Display'} decimal places must be between 0 and 10`;
  }
  return null;
};

const validateHierarchyConfig = (
  hierarchyConfig: any,
  availableFieldDefinitions: FieldDefinition[],
  currentFieldDefId: string
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!hierarchyConfig.hierarchy_label || hierarchyConfig.hierarchy_label.trim().length < 2) {
    errors.hierarchy_label = 'Hierarchy label must be at least 2 characters';
  } else if (hierarchyConfig.hierarchy_label.length > 100) {
    errors.hierarchy_label = 'Hierarchy label must be under 100 characters';
  }

  if (hierarchyConfig.parent_fielddef_id) {
    const parentExists = availableFieldDefinitions.some(
      (fd) => fd.fielddef_id === hierarchyConfig.parent_fielddef_id
    );
    if (!parentExists && hierarchyConfig.parent_fielddef_id !== currentFieldDefId) {
      errors.parent_fielddef_id =
        'Parent field definition must be a valid list-type field definition';
    }
    if (hierarchyConfig.parent_fielddef_id === currentFieldDefId) {
      errors.parent_fielddef_id = 'Field definition cannot be its own parent';
    }
  }

  return errors;
};

// Form initialization helper functions
const initializeEditModeFormData = (fieldDefinition: FieldDefinition): FieldDefinitionFormData => {
  // Clean hierarchy_label if it exists
  let cleanedHierarchyConfig = fieldDefinition.hierarchy_config;
  if (cleanedHierarchyConfig && cleanedHierarchyConfig.hierarchy_label) {
    cleanedHierarchyConfig = {
      ...cleanedHierarchyConfig,
      hierarchy_label: cleanHierarchyLabel(cleanedHierarchyConfig.hierarchy_label),
    };
  }

  return {
    fielddef_id: fieldDefinition.fielddef_id,
    tenant_id: fieldDefinition.tenant_id,
    label: fieldDefinition.label,
    description: fieldDefinition.description || null,
    datatype: fieldDefinition.datatype,
    is_multiselect: fieldDefinition.is_multiselect,
    creation_access: fieldDefinition.creation_access,
    storage_decimal_places: fieldDefinition.storage_decimal_places ?? null,
    display_decimal_places: fieldDefinition.display_decimal_places ?? null,
    access: fieldDefinition.access,
    hierarchy_config: cleanedHierarchyConfig || null,
    sourcevalue_fielddef_id: fieldDefinition.sourcevalue_fielddef_id || null,
    sourcevalue_filter: fieldDefinition.sourcevalue_filter || null,
    file_config: fieldDefinition.file_config || null,
  };
};

const initializeCreateModeFormData = (tenantContext: TenantInfo): FieldDefinitionFormData => {
  return {
    fielddef_id: '',
    tenant_id: tenantContext.tenant_id,
    label: '',
    description: null,
    datatype: 'text',
    is_multiselect: false,
    creation_access: undefined,
    storage_decimal_places: null,
    display_decimal_places: null,
    access: tenantContext.is_global
      ? ACCESS_LEVELS.CLIENT_OVERRIDEABLE
      : ACCESS_LEVELS.CLIENT_READ_ONLY,
    hierarchy_config: null,
    sourcevalue_fielddef_id: null,
    sourcevalue_filter: null,
    file_config: null,
  };
};

// Helper functions for complex UI logic
const getChildFieldDisplayValue = (
  childFielddefId: string | null | undefined,
  allFieldDefinitions: FieldDefinition[]
): string => {
  if (!childFielddefId) return 'None';

  const childDef = allFieldDefinitions.find((fd) => fd.fielddef_id === childFielddefId);
  return childDef?.label || childFielddefId;
};

const getSourceFieldSelectValue = (
  sourceFielddefId: string | null,
  availableSourceFields: FieldDefinition[]
): string => {
  if (!sourceFielddefId) return '';

  return availableSourceFields.some((fd) => fd.fielddef_id === sourceFielddefId)
    ? sourceFielddefId
    : '';
};

interface FieldDefinitionModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  fieldDefinition?: FieldDefinition;
  tenantContext: TenantInfo;
  canEdit: boolean;
  onClose: () => void;
  onSave: (data: FieldDefinitionFormData) => Promise<void>;
  onDelete?: (fieldDefinition: FieldDefinition) => Promise<void>;
}

// Modal component for create/edit
const FieldDefinitionModal: React.FC<FieldDefinitionModalProps> = ({
  open,
  mode,
  fieldDefinition,
  tenantContext,
  canEdit,
  onClose,
  onSave,
  onDelete,
}) => {
  // Calculate if user can actually edit based on access level permissions
  const getEffectiveCanEdit = () => {
    if (!canEdit) return false;

    // If creating a new field definition or editing in GLOBAL context, use base canEdit
    if (mode === 'create' || tenantContext.is_global) {
      return canEdit;
    }

    // If editing a GLOBAL field definition from non-GLOBAL context, check access level
    if (fieldDefinition?.tenant_id === 'GLOBAL') {
      const accessLevel = fieldDefinition.access;
      // Internal Only and Client Read-Only are not editable
      return (
        accessLevel !== ACCESS_LEVELS.INTERNAL_ONLY &&
        accessLevel !== ACCESS_LEVELS.CLIENT_READ_ONLY
      );
    }

    return canEdit;
  };

  const effectiveCanEdit = getEffectiveCanEdit();

  // Form data state
  const [formData, setFormData] = useState<FieldDefinitionFormData>({
    fielddef_id: '',
    tenant_id: tenantContext.tenant_id,
    label: '',
    description: null,
    datatype: 'text',
    is_multiselect: false,
    creation_access: undefined,
    storage_decimal_places: null,
    display_decimal_places: null,
    access: tenantContext.is_global
      ? ACCESS_LEVELS.CLIENT_OVERRIDEABLE
      : ACCESS_LEVELS.CLIENT_READ_ONLY,
  });

  // Consolidated loading states
  const [loadingStates, setLoadingStates] = useState({
    saving: false,
    fieldDefinitions: false,
    hierarchyLabels: false,
    sourceFields: false,
  });

  // Other state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [_generatedId, setGeneratedId] = useState<string>('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [availableFieldDefinitions, setAvailableFieldDefinitions] = useState<FieldDefinition[]>([]);
  const [allFieldDefinitions, setAllFieldDefinitions] = useState<FieldDefinition[]>([]); // All field defs for child lookup
  const [hierarchyLabels, setHierarchyLabels] = useState<string[]>([]);
  const [sourceFieldDefinitions, setSourceFieldDefinitions] = useState<FieldDefinition[]>([]);
  const [sourceFilterError, setSourceFilterError] = useState<string | null>(null);
  const [hierarchyExpanded, setHierarchyExpanded] = useState(!!fieldDefinition?.hierarchy_config);
  const [sourceFieldExpanded, setSourceFieldExpanded] = useState(
    !!fieldDefinition?.sourcevalue_fielddef_id
  );

  // Ref for hierarchy label input
  const hierarchyLabelInputRef = useRef<HTMLInputElement>(null);

  // Sync accordion expansion with formData
  useEffect(() => {
    setHierarchyExpanded(!!formData.hierarchy_config);
  }, [formData.hierarchy_config]);

  useEffect(() => {
    setSourceFieldExpanded(!!formData.sourcevalue_fielddef_id);
  }, [formData.sourcevalue_fielddef_id]);

  // Generate field definition ID from label (frontend implementation)
  const handleGenerateId = (label: string) => {
    if (!label.trim()) {
      setGeneratedId('');
      setFormData((prev) => ({ ...prev, fielddef_id: '' }));
      return;
    }

    const fieldId = generateFieldDefinitionId(label);
    setGeneratedId(fieldId);
    setFormData((prev) => ({ ...prev, fielddef_id: fieldId }));
  };

  // Load available field definitions for hierarchy parent selection and child lookup
  useEffect(() => {
    const loadFieldDefinitions = async () => {
      if (open && formData.datatype === 'list') {
        setLoadingStates((prev) => ({ ...prev, fieldDefinitions: true }));
        try {
          const fieldDefs = await metadataService.getFieldDefinitions({
            tenant_id: tenantContext.tenant_id,
            status: 'active',
            datatype: 'list',
          });
          // Store all field definitions for child lookup
          setAllFieldDefinitions(fieldDefs);
          // Filter to only include hierarchical field definitions (those with hierarchy_config)
          // and exclude current field definition to prevent self-reference
          const filtered = fieldDefs.filter((fd) => {
            // Must have hierarchy_config to be a valid parent
            if (!fd.hierarchy_config) return false;
            // Exclude current field definition
            if (mode === 'edit' && fd.fielddef_id === fieldDefinition?.fielddef_id) return false;
            return true;
          });
          setAvailableFieldDefinitions(filtered);
        } catch (error) {
          console.error('Failed to load field definitions:', error);
        } finally {
          setLoadingStates((prev) => ({ ...prev, fieldDefinitions: false }));
        }
      } else {
        setAvailableFieldDefinitions([]);
        setAllFieldDefinitions([]);
      }
    };
    loadFieldDefinitions();
  }, [open, formData.datatype, tenantContext.tenant_id, mode, fieldDefinition?.fielddef_id]);

  // Load hierarchy labels for autocomplete
  useEffect(() => {
    const loadHierarchyLabels = async () => {
      if (open && formData.datatype === 'list' && formData.hierarchy_config) {
        setLoadingStates((prev) => ({ ...prev, hierarchyLabels: true }));
        try {
          const labels = await metadataService.getHierarchyLabels(tenantContext.tenant_id);
          setHierarchyLabels(labels);
        } catch (error) {
          console.error('Failed to load hierarchy labels:', error);
        } finally {
          setLoadingStates((prev) => ({ ...prev, hierarchyLabels: false }));
        }
      } else {
        setHierarchyLabels([]);
      }
    };
    loadHierarchyLabels();
  }, [open, formData.datatype, formData.hierarchy_config, tenantContext.tenant_id]);

  // Load available source field definitions (list types only, excluding current field)
  useEffect(() => {
    const loadSourceFieldDefinitions = async () => {
      if (open && formData.datatype === 'list') {
        setLoadingStates((prev) => ({ ...prev, sourceFields: true }));
        try {
          const fieldDefs = await metadataService.getListFieldDefinitions(
            tenantContext.tenant_id,
            mode === 'edit' ? fieldDefinition?.fielddef_id : undefined
          );
          setSourceFieldDefinitions(fieldDefs);
        } catch (error) {
          console.error('Failed to load source field definitions:', error);
          setSourceFieldDefinitions([]);
        } finally {
          setLoadingStates((prev) => ({ ...prev, sourceFields: false }));
        }
      } else {
        setSourceFieldDefinitions([]);
      }
    };

    loadSourceFieldDefinitions();
  }, [open, formData.datatype, tenantContext.tenant_id, mode, fieldDefinition?.fielddef_id]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && fieldDefinition) {
        // Set loading state for source fields if this is a list type
        // This prevents MUI warnings about out-of-range values while options load
        if (fieldDefinition.datatype === 'list' && fieldDefinition.sourcevalue_fielddef_id) {
          setLoadingStates((prev) => ({ ...prev, sourceFields: true }));
        }

        setFormData(initializeEditModeFormData(fieldDefinition));
        setGeneratedId(fieldDefinition.fielddef_id);
      } else {
        setFormData(initializeCreateModeFormData(tenantContext));
        setGeneratedId('');
      }
      setErrors({});
    }
  }, [open, mode, fieldDefinition, tenantContext]);

  // Auto-generate ID when label changes (create mode only)
  useEffect(() => {
    if (mode === 'create' && formData.label.trim()) {
      // Update with each keypress - only in create mode
      handleGenerateId(formData.label);
    }
    // In edit mode, don't auto-generate ID even if label changes
  }, [formData.label, mode]);

  // Validate form data
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate field definition ID
    const fieldDefIdError = validateFieldDefId(formData.fielddef_id, metadataService);
    if (fieldDefIdError) newErrors.fielddef_id = fieldDefIdError;

    // Validate label
    const labelError = validateLabel(formData.label);
    if (labelError) newErrors.label = labelError;

    // Validate description
    const descriptionError = descriptionLengthError(formData.description ?? null);
    if (descriptionError) newErrors.description = descriptionError;

    // Validate decimal places for numeric types
    if (shouldShowDecimalPlaces(formData.datatype)) {
      const storageError = validateDecimalPlaces(formData.storage_decimal_places, 'storage');
      if (storageError) newErrors.storage_decimal_places = storageError;

      const displayError = validateDecimalPlaces(formData.display_decimal_places, 'display');
      if (displayError) newErrors.display_decimal_places = displayError;
    }

    // Validate hierarchy configuration
    if (formData.datatype === 'list' && formData.hierarchy_config) {
      const hierarchyErrors = validateHierarchyConfig(
        formData.hierarchy_config,
        availableFieldDefinitions,
        formData.fielddef_id
      );
      Object.assign(newErrors, hierarchyErrors);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoadingStates((prev) => ({ ...prev, saving: true }));
      setSaveError(null);
      setSourceFilterError(null);

      // Validate source filter if configured
      if (formData.sourcevalue_fielddef_id && formData.sourcevalue_filter) {
        try {
          await metadataService.validateSourceFilter(
            formData.sourcevalue_fielddef_id,
            formData.sourcevalue_filter,
            tenantContext.tenant_id
          );
        } catch (validationError: any) {
          setSourceFilterError(
            validationError.response?.data?.detail ||
              'Invalid filter configuration. Please check your conditions.'
          );
          setLoadingStates((prev) => ({ ...prev, saving: false }));
          return;
        }
      }

      // Prepare form data: remove hierarchy_level and child_fielddef_id (auto-calculated/managed by backend)
      // Also ensure hierarchy_label is captured from autocomplete inputValue if needed
      let hierarchyConfig = formData.hierarchy_config;
      if (hierarchyConfig) {
        // Check if hierarchy_label is empty but we have an autocomplete input value
        // This can happen if the user types but doesn't blur/select before submitting
        if (
          hierarchyLabelInputRef.current &&
          (!hierarchyConfig.hierarchy_label || hierarchyConfig.hierarchy_label.trim() === '')
        ) {
          const inputValue = hierarchyLabelInputRef.current.value?.trim() || '';
          if (inputValue) {
            hierarchyConfig = {
              ...hierarchyConfig,
              hierarchy_label: inputValue,
            };
          }
        }

        // Only include hierarchy_config if hierarchy_label is present and non-empty
        if (!hierarchyConfig.hierarchy_label || hierarchyConfig.hierarchy_label.trim() === '') {
          hierarchyConfig = null;
        }
      }

      const dataToSave: FieldDefinitionFormData = {
        ...formData,
        hierarchy_config: hierarchyConfig
          ? {
              ...hierarchyConfig,
              // Remove hierarchy_level - will be auto-calculated by backend
              hierarchy_level: undefined as any,
              // Remove child_fielddef_id - will be auto-managed by backend
              child_fielddef_id: undefined as any,
            }
          : null,
      };

      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error('Failed to save field definition:', error);
      const message = error instanceof Error ? error.message : 'Failed to save field definition';
      setSaveError(message);
    } finally {
      setLoadingStates((prev) => ({ ...prev, saving: false }));
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!fieldDefinition || !onDelete) return;

    try {
      setLoadingStates((prev) => ({ ...prev, saving: true }));
      await onDelete(fieldDefinition);
      onClose();
    } catch (error) {
      console.error('Failed to delete field definition:', error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, saving: false }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {tenantContext.is_global ? <GlobalIcon /> : <TenantIcon />}
          {mode === 'create' ? 'Create Field Definition' : 'Edit Field Definition'}
          <Chip
            label={tenantContext.name}
            size="small"
            color={tenantContext.is_global ? 'warning' : 'primary'}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="field-definition-save-error">
            {saveError}
          </Alert>
        )}
        {tenantContext.is_global && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are creating/editing a GLOBAL field definition. This will affect all tenants.
          </Alert>
        )}

        {!tenantContext.is_global && fieldDefinition?.tenant_id === 'GLOBAL' && mode === 'edit' && (
          <>
            {fieldDefinition.access === ACCESS_LEVELS.CLIENT_READ_ONLY ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This GLOBAL field definition is read-only in your tenant context. You cannot create
                an override for this field.
              </Alert>
            ) : fieldDefinition.access === ACCESS_LEVELS.INTERNAL_ONLY ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                This GLOBAL field definition is internal only and not accessible in your tenant
                context.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                You are editing a GLOBAL field definition. This will create a tenant-specific
                override instead of modifying the GLOBAL definition.
              </Alert>
            )}
          </>
        )}

        <Box sx={{ mt: 1 }} data-testid="field-definition-form">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Display Label"
                required
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                error={!!errors.label}
                helperText={errors.label || 'Human-readable label for the field'}
                fullWidth
                disabled={!effectiveCanEdit}
                data-testid="display-label-input"
                inputProps={{ 'aria-label': 'Display Label' }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Field Definition ID"
                value={formData.fielddef_id}
                error={!!errors.fielddef_id}
                helperText={
                  mode === 'create'
                    ? 'Will be auto-generated from label'
                    : 'Field definition ID (read-only)'
                }
                fullWidth
                disabled={true} // Always disabled - not editable
                placeholder={mode === 'create' ? 'Auto-generated from label' : 'fd_example-field'}
                data-testid="field-definition-id-input"
                inputProps={{ 'aria-label': 'Field Definition ID' }}
                InputProps={{}}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth disabled={!effectiveCanEdit || mode === 'edit'}>
              <InputLabel required>Data Type</InputLabel>
              <Select
                value={formData.datatype}
                onChange={(e: SelectChangeEvent) => {
                  const newDatatype = e.target.value as FieldDefinition['datatype'];
                  const updates: Partial<FieldDefinitionFormData> = {
                    datatype: newDatatype,
                    // Reset is_multiselect to false if changing from list to other datatype
                    is_multiselect: newDatatype === 'list' ? formData.is_multiselect : false,
                  };

                  // Set defaults for decimal places when switching to numeric types
                  if (shouldShowDecimalPlaces(newDatatype)) {
                    // Only set defaults if not already set (preserve existing values when editing)
                    if (
                      formData.storage_decimal_places === null ||
                      formData.storage_decimal_places === undefined
                    ) {
                      updates.storage_decimal_places = DECIMAL_DEFAULTS.STORAGE;
                    }
                    if (
                      formData.display_decimal_places === null ||
                      formData.display_decimal_places === undefined
                    ) {
                      updates.display_decimal_places = DECIMAL_DEFAULTS.DISPLAY;
                    }
                  } else {
                    // Clear decimal places when switching away from numeric types
                    updates.storage_decimal_places = null;
                    updates.display_decimal_places = null;
                  }

                  // Clear hierarchy config and source config when switching away from list type
                  if (newDatatype !== 'list') {
                    updates.hierarchy_config = null;
                    updates.sourcevalue_fielddef_id = null;
                    updates.sourcevalue_filter = null;
                  }

                  // Handle file config when switching to/from file type
                  if (newDatatype === 'file') {
                    updates.file_config = DEFAULT_FILE_CONFIG;
                  } else if (formData.datatype === 'file') {
                    updates.file_config = null;
                  }

                  setFormData({ ...formData, ...updates });
                  setSourceFilterError(null);
                }}
                label="Data Type"
                data-testid="data-type-select"
              >
                <MenuItem value="text" data-value="text">
                  Text
                </MenuItem>
                <MenuItem value="list" data-value="list">
                  List
                </MenuItem>
                <MenuItem value="bool" data-value="bool">
                  Boolean
                </MenuItem>
                <MenuItem value="currency" data-value="currency">
                  Currency
                </MenuItem>
                <MenuItem value="decimal" data-value="decimal">
                  Decimal
                </MenuItem>
                <MenuItem value="percent" data-value="percent">
                  Percentage
                </MenuItem>
                <MenuItem value="date" data-value="date">
                  Date
                </MenuItem>
                <MenuItem value="file" data-value="file">
                  File Upload
                </MenuItem>
              </Select>
              <FormHelperText>Selects the type of data stored by this field</FormHelperText>
            </FormControl>
          </Box>

          {/* Access Level field - only visible for GLOBAL field definitions with list datatype */}
          {tenantContext.is_global && formData.datatype === 'list' && (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth disabled={!canEdit}>
                <InputLabel>MDM Access Level</InputLabel>
                <Select
                  value={formData.access.toString()}
                  onChange={(e: SelectChangeEvent) =>
                    setFormData({
                      ...formData,
                      access: e.target.value as FieldDefinition['access'],
                    })
                  }
                  label="MDM Access Level"
                  data-testid="access-level-select"
                >
                  <MenuItem value="0">Internal Only</MenuItem>
                  <MenuItem value="1">Client Read-Only</MenuItem>
                  <MenuItem value="2">Client Overrideable</MenuItem>
                  <MenuItem value="3">Client Overrideable All</MenuItem>
                </Select>
                <FormHelperText>Selects security level of MDM data for clients</FormHelperText>
              </FormControl>
            </Box>
          )}

          {/* Creation Access - only show for list datatypes */}
          {formData.datatype === 'list' && (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth disabled={!effectiveCanEdit}>
                <InputLabel>Creation Access</InputLabel>
                <Select
                  value={formData.creation_access || ''}
                  onChange={(e: SelectChangeEvent) =>
                    setFormData({
                      ...formData,
                      creation_access: (e.target.value as 'User' | 'Admin' | 'None') || undefined,
                    })
                  }
                  label="Creation Access"
                >
                  <MenuItem value="User">User (any user can add new values)</MenuItem>
                  <MenuItem value="Admin">Admin (administrators can add new values)</MenuItem>
                  <MenuItem value="None">None (MDM admin only)</MenuItem>
                </Select>
                <FormHelperText>Who can add new values on the fly</FormHelperText>
              </FormControl>
            </Box>
          )}

          {/* Multi-select checkbox - only show for list datatypes */}
          {formData.datatype === 'list' && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_multiselect}
                    onChange={(e) => setFormData({ ...formData, is_multiselect: e.target.checked })}
                    disabled={!effectiveCanEdit}
                    data-testid="multiselect-checkbox"
                  />
                }
                label="Enable multi-select (allows multiple values to be selected)"
              />
            </Box>
          )}

          {/* Description field */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12 }}>
              <DescriptionField
                label="Description"
                value={formData.description || ''}
                onChange={(value) => setFormData({ ...formData, description: value || null })}
                error={!!errors.description}
                helperText={errors.description || 'Optional description of the field'}
                fullWidth
                disabled={!effectiveCanEdit}
              />
            </Grid>
          </Grid>

          {/* Storage Decimal Places - only show for numeric datatypes */}
          {shouldShowDecimalPlaces(formData.datatype) && (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Storage Decimal Places"
                type="number"
                required
                value={formData.storage_decimal_places ?? ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                  setFormData({ ...formData, storage_decimal_places: value });
                }}
                error={!!errors.storage_decimal_places}
                helperText={
                  errors.storage_decimal_places || 'Number of decimal places to store (0-10)'
                }
                fullWidth
                inputProps={{ min: 0, max: 10, required: true }}
                disabled={!effectiveCanEdit}
              />
            </Box>
          )}

          {/* Display Decimal Places - only show for numeric datatypes */}
          {shouldShowDecimalPlaces(formData.datatype) && (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Display Decimal Places"
                type="number"
                required
                value={formData.display_decimal_places ?? ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                  setFormData({ ...formData, display_decimal_places: value });
                }}
                error={!!errors.display_decimal_places}
                helperText={
                  errors.display_decimal_places ||
                  'Number of decimal places to display (0-10, rounded from storage)'
                }
                fullWidth
                inputProps={{ min: 0, max: 10, required: true }}
                disabled={!effectiveCanEdit}
              />
            </Box>
          )}

          {/* Hierarchy Configuration Section - only show for list datatypes */}
          {formData.datatype === 'list' && (
            <>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ borderTop: 1, borderColor: 'divider', paddingTop: 2 }}>
                  <Accordion
                    expanded={hierarchyExpanded}
                    onChange={(_, isExpanded) => {
                      setHierarchyExpanded(isExpanded);
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">Hierarchy</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Autocomplete
                            freeSolo
                            options={hierarchyLabels}
                            value={formData.hierarchy_config?.hierarchy_label || null}
                            inputValue={formData.hierarchy_config?.hierarchy_label || ''}
                            onInputChange={(_, newValue, _reason) => {
                              // Clean the value using utility function
                              const cleanedValue = cleanHierarchyLabel(newValue);

                              if (cleanedValue.trim()) {
                                // Create or update hierarchy_config when value is entered
                                setFormData({
                                  ...formData,
                                  hierarchy_config: {
                                    hierarchy_label: cleanedValue,
                                    hierarchy_level:
                                      formData.hierarchy_config?.hierarchy_level || 1,
                                    parent_fielddef_id:
                                      formData.hierarchy_config?.parent_fielddef_id || null,
                                    child_fielddef_id:
                                      formData.hierarchy_config?.child_fielddef_id || null,
                                  },
                                });
                              } else if (!cleanedValue.trim() && formData.hierarchy_config) {
                                // Remove hierarchy_config when value is cleared
                                setFormData({
                                  ...formData,
                                  hierarchy_config: undefined,
                                });
                              }
                            }}
                            onChange={(_, newValue) => {
                              // Handle both string (freeSolo) and option selection
                              const labelValue =
                                typeof newValue === 'string'
                                  ? cleanHierarchyLabel(newValue)
                                  : newValue || '';

                              if (labelValue.trim()) {
                                // Create or update hierarchy_config when value is entered
                                setFormData({
                                  ...formData,
                                  hierarchy_config: {
                                    hierarchy_label: labelValue,
                                    hierarchy_level:
                                      formData.hierarchy_config?.hierarchy_level || 1,
                                    parent_fielddef_id:
                                      formData.hierarchy_config?.parent_fielddef_id || null,
                                    child_fielddef_id:
                                      formData.hierarchy_config?.child_fielddef_id || null,
                                  },
                                });
                              } else if (!labelValue.trim() && formData.hierarchy_config) {
                                // Remove hierarchy_config when value is cleared
                                setFormData({
                                  ...formData,
                                  hierarchy_config: undefined,
                                });
                              }
                            }}
                            loading={loadingStates.hierarchyLabels}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Hierarchy Label"
                                error={!!errors.hierarchy_label}
                                helperText={
                                  errors.hierarchy_label ||
                                  'Human-readable label for the hierarchy (e.g., "Geography", "Product Taxonomy"). Autocomplete suggests existing labels.'
                                }
                                disabled={!effectiveCanEdit}
                                data-testid="hierarchy-label-input"
                                inputRef={hierarchyLabelInputRef}
                                inputProps={{
                                  ...params.inputProps,
                                  minLength: 2,
                                  maxLength: 100,
                                }}
                              />
                            )}
                            fullWidth
                          />
                        </Box>

                        <Box>
                          <FormControl
                            fullWidth
                            disabled={!effectiveCanEdit || loadingStates.fieldDefinitions}
                          >
                            <InputLabel>Parent Field Definition</InputLabel>
                            <Select
                              value={formData.hierarchy_config?.parent_fielddef_id || ''}
                              onChange={(e: SelectChangeEvent) => {
                                const parentId = e.target.value || null;
                                if (formData.hierarchy_config) {
                                  setFormData({
                                    ...formData,
                                    hierarchy_config: {
                                      ...formData.hierarchy_config,
                                      parent_fielddef_id: parentId || null,
                                    },
                                  });
                                }
                              }}
                              label="Parent Field Definition"
                              data-testid="parent-fielddef-select"
                            >
                              <MenuItem value="">None (Top Level)</MenuItem>
                              {availableFieldDefinitions
                                .filter((fd) => {
                                  // Filter out current field and prevent circular references
                                  if (fd.fielddef_id === formData.fielddef_id) return false;
                                  // TODO: Add circular reference check
                                  return true;
                                })
                                .map((fd) => (
                                  <MenuItem key={fd.fielddef_id} value={fd.fielddef_id}>
                                    {fd.label} ({fd.fielddef_id})
                                  </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                              Leave blank to indicate the top level of the hierarchy
                            </FormHelperText>
                          </FormControl>
                        </Box>

                        {/* Child Field Definition (Read-only, informational) */}
                        <Box>
                          <TextField
                            label="Child Field Definition"
                            value={getChildFieldDisplayValue(
                              formData.hierarchy_config?.child_fielddef_id,
                              allFieldDefinitions
                            )}
                            disabled
                            fullWidth
                            data-testid="child-fielddef-display"
                          />
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </Box>

              {/* Source Field Definition Configuration */}
              <Box sx={{ mt: 2 }}>
                <Accordion
                  expanded={sourceFieldExpanded}
                  onChange={(_, isExpanded) => setSourceFieldExpanded(isExpanded)}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Source Field Definition</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 2,
                      }}
                    >
                      {/* Source Field Selector */}
                      <Box>
                        <FormControl
                          fullWidth
                          disabled={!effectiveCanEdit || loadingStates.sourceFields}
                        >
                          <InputLabel>Source Field Definition</InputLabel>
                          <Select
                            value={getSourceFieldSelectValue(
                              formData.sourcevalue_fielddef_id ?? null,
                              sourceFieldDefinitions
                            )}
                            onChange={(e: SelectChangeEvent) => {
                              const sourceId = e.target.value || null;
                              setFormData({
                                ...formData,
                                sourcevalue_fielddef_id: sourceId,
                                // Clear filter if source is cleared
                                sourcevalue_filter: sourceId ? formData.sourcevalue_filter : null,
                              });
                              setSourceFilterError(null);
                            }}
                            label="Source Field Definition"
                            data-testid="source-field-select"
                          >
                            <MenuItem value="">None</MenuItem>
                            {sourceFieldDefinitions.map((fd) => (
                              <MenuItem key={fd.fielddef_id} value={fd.fielddef_id}>
                                {fd.label}
                                {fd.tenant_id === 'GLOBAL' && (
                                  <Chip
                                    label="GLOBAL"
                                    size="small"
                                    sx={{ ml: 1 }}
                                    icon={<GlobalIcon />}
                                  />
                                )}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Configure this field to derive values from another field definition.
                          </FormHelperText>
                        </FormControl>
                      </Box>

                      {/* Filter Configuration */}
                      {formData.sourcevalue_fielddef_id && (
                        <Box>
                          <Box sx={{ pl: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Filter Conditions
                            </Typography>
                            <Typography variant="caption" color="textSecondary" paragraph>
                              Define conditions to filter which values from the source field are
                              displayed. Leave empty to show all values.
                            </Typography>

                            <SourceFieldFilterBuilder
                              sourceFielddefId={formData.sourcevalue_fielddef_id}
                              tenantId={tenantContext.tenant_id}
                              value={formData.sourcevalue_filter || null}
                              onChange={(filter) => {
                                setFormData({
                                  ...formData,
                                  sourcevalue_filter: filter,
                                });
                                setSourceFilterError(null);
                              }}
                              disabled={!effectiveCanEdit}
                              error={sourceFilterError || undefined}
                            />
                          </Box>
                        </Box>
                      )}

                      {/* Source Field Info Alert */}
                      {formData.sourcevalue_fielddef_id && (
                        <Box>
                          <Alert severity="info">
                            <strong>Note:</strong> This field will be read-only in the MDM Manager.
                            Values are dynamically retrieved from the source field definition and
                            cannot be edited directly.
                          </Alert>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </>
          )}

          {/* File Configuration Section - only show for file datatypes */}
          {formData.datatype === 'file' && (
            <FileConfigurationSection
              config={formData.file_config as FileConfig | null}
              onChange={(newConfig) => {
                setFormData({
                  ...formData,
                  file_config: newConfig,
                });
              }}
              disabled={!effectiveCanEdit}
              errors={errors}
            />
          )}
        </Box>
      </DialogContent>

      <DialogFooter
        destructiveAction={
          mode === 'edit' &&
          onDelete &&
          effectiveCanEdit && (
            <DestructiveButton
              onClick={handleDelete}
              disabled={loadingStates.saving}
              startIcon={loadingStates.saving ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              Delete
            </DestructiveButton>
          )
        }
      >
        <SecondaryButton onClick={onClose} disabled={loadingStates.saving}>
          Cancel
        </SecondaryButton>

        {effectiveCanEdit && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loadingStates.saving}
            startIcon={loadingStates.saving ? <CircularProgress size={16} /> : undefined}
          >
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
};

export default FieldDefinitionModal;
